import React, { useEffect, useRef, useState } from 'react';
import { X, BadgeCheck, Check } from 'lucide-react';
import {
  subscribeToLatestMessages,
  sendChatMessage,
  isAanganSupportSender,
  AANGAN_SUPPORT_DISPLAY_NAME,
  AANGAN_SUPPORT_UID,
} from '../../lib/db';
import './SupportChatModal.css';

// ============================================================
// SupportChatModal — Session 5a (business side).
//
// Bare-bones support chat surface that opens when a business owner
// clicks "Chat with Aangan support" on the Manage Business screen.
// Deliberately simple for MVP:
//   - live subscription to the chat's messages
//   - textarea + send button (Enter to send, Shift+Enter for newline)
//   - admin messages display sender name as "Aangan Support"
//     (regardless of which real admin UID authored — see
//     isAanganSupportSender in db.js)
//   - no attachments, no read receipts, no typing state
//
// Session 5c — PLAN CARDS. Admin can send a structured Aangan Connect
// plan (type: 'plan-card', planCard: { tierId, amount, durationMonths,
// label }). Rendered here as a card with a one-tap "I'd like this plan"
// CTA. Because message docs are immutable in firestore.rules
// (`allow update: if false`), the tap cannot mark the card itself as
// accepted — it sends a normal text REPLY, which the admin reads as
// intent before activating via the admin panel's ActivationModal.
//
// The parent (ManageBusinessTab) owns the chatId — this component
// receives it as a prop after openOrCreateSupportChat resolves.
// ============================================================

function fmtTime(ts) {
  if (!ts) return '';
  let d;
  if (typeof ts?.toDate === 'function') d = ts.toDate();
  else if (ts instanceof Date) d = ts;
  else if (typeof ts?.seconds === 'number') d = new Date(ts.seconds * 1000);
  else return '';
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// ---- Plan card bubble (Session 5c) ---------------------------------
//
// Renders msg.planCard sent by an admin. Reads straight off the message
// doc — no shared import with the admin tree, the field names ARE the
// contract. Falls back gracefully: if planCard is malformed we render
// msg.text (which always carries a readable "Aangan Connect plan: ₹199
// · 1 month" fallback), so a bad card degrades to a plain bubble rather
// than an empty one.
//
// The CTA is disabled once acked in this session (acked prop) to stop
// double-sends; the durable record is the reply message itself.
function PlanCardBubble({ card, fallbackText, onAccept, acked, sending }) {
  if (!card || typeof card.amount !== 'number') {
    return <div className="support-msg-bubble">{fallbackText || ''}</div>;
  }

  return (
    <div className="support-plan-card">
      <div className="support-plan-card-head">
        <BadgeCheck size={15} />
        <span>Aangan Connect</span>
      </div>

      <div className="support-plan-card-price">
        <span className="support-plan-card-amount">₹{card.amount}</span>
        <span className="support-plan-card-duration">for {card.label}</span>
      </div>

      <ul className="support-plan-card-perks">
        <li>Customers can contact you and place orders</li>
        <li>Your listing stays live in the directory</li>
        <li>Post updates and manage your catalogue</li>
      </ul>

      <button
        type="button"
        className="support-plan-card-cta"
        onClick={() => onAccept(card)}
        disabled={acked || sending}
      >
        {acked
          ? <><Check size={13} /> Sent to Aangan</>
          : (sending ? 'Sending…' : "I'd like this plan")}
      </button>

      <div className="support-plan-card-foot">
        Aangan will share payment details in this chat.
      </div>
    </div>
  );
}

export default function SupportChatModal({ chatId, ownerUid, onClose, prefillText }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  // Session 5c — plan-card IDs the owner has tapped in THIS session.
  // Purely a double-tap guard for the button's disabled state. The
  // durable acknowledgment is the reply message in the thread, so this
  // does not need to survive a remount.
  const [ackedCardIds, setAckedCardIds] = useState([]);

  const scrollRef = useRef(null);

  // Session 6c2a — Pre-fill the composer on mount when the parent
  // provides a suggested opening message (e.g. the reactivate flow
  // pre-fills "Hi, I'd like to reactivate my Aangan Connect
  // subscription for X…"). Runs ONCE — deliberately doesn't refill
  // on prop change so we don't clobber user edits. The user can
  // review, edit, or send as-is.
  const prefillAppliedRef = useRef(false);
  useEffect(() => {
    if (prefillAppliedRef.current) return;
    if (!prefillText) return;
    if (replyText) return; // Don't clobber existing draft.
    setReplyText(prefillText);
    prefillAppliedRef.current = true;
  }, [prefillText, replyText]);

  // Live subscription to the chat's messages. Cleaned up on close /
  // unmount to prevent leaked listeners.
  useEffect(() => {
    if (!chatId) return;

    setLoading(true);
    const unsub = subscribeToLatestMessages(chatId, 50, (msgs) => {
      setMessages(msgs);
      setLoading(false);
    });

    return () => {
      try { unsub?.(); } catch (_) { /* ignore */ }
    };
  }, [chatId]);

  // Auto-scroll on new messages.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!chatId || sending) return;
    const trimmed = replyText.trim();
    if (!trimmed) return;

    setSending(true);
    setSendError(null);
    try {
      // Business owner uses V1's sendChatMessage — safe here because
      // this is a peer-shaped send (owner is a real participant, and
      // participants=[ownerUid, AANGAN_SUPPORT_UID].sort() matches what
      // was set at chat creation). Admin sends use a different helper
      // (sendAdminMessage in super-admin/lib/supportChats.js) because
      // admin isn't a participant.
      await sendChatMessage({
        chatId,
        fromId: ownerUid,
        toId: AANGAN_SUPPORT_UID,
        text: trimmed,
      });
      setReplyText('');
      // Message appears via live subscription; no state update here.
    } catch (err) {
      console.error('[SupportChatModal] send failed:', err);
      setSendError(err?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Session 5c — one-tap plan acknowledgment.
  //
  // Sends a plain text reply (NOT a structured message) because:
  //   a) message docs are immutable, so we can't mark the card accepted;
  //   b) a readable sentence is what the admin actually needs to act on;
  //   c) it reuses the owner-side send path with zero rules surface.
  //
  // Admin then activates via the panel's ActivationModal, which fires the
  // Session 6d push telling the merchant Aangan Connect is live.
  const handleAcceptPlan = async (card) => {
    if (!chatId || sending) return;
    if (ackedCardIds.includes(card.tierId)) return;

    setSending(true);
    setSendError(null);
    try {
      await sendChatMessage({
        chatId,
        fromId: ownerUid,
        toId: AANGAN_SUPPORT_UID,
        text: `I'd like the ₹${card.amount} · ${card.label} Aangan Connect plan. Please share payment details.`,
      });
      setAckedCardIds((prev) => [...prev, card.tierId]);
    } catch (err) {
      console.error('[SupportChatModal] plan accept failed:', err);
      setSendError(err?.message || 'Could not send — try again');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="support-modal-backdrop" onClick={sending ? undefined : onClose}>
      <div
        className="support-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="support-modal-header">
          <div className="support-modal-header-avatar">🏛️</div>
          <div className="support-modal-header-text">
            <div className="support-modal-header-name">{AANGAN_SUPPORT_DISPLAY_NAME}</div>
            <div className="support-modal-header-sub">Aangan · Usually replies within a day</div>
          </div>
          <button
            className="support-modal-close"
            onClick={onClose}
            aria-label="Close"
            type="button"
            disabled={sending}
          >
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <div className="support-modal-messages" ref={scrollRef}>
          {loading && <div className="support-modal-empty">Loading…</div>}

          {!loading && messages.length === 0 && (
            <div className="support-modal-empty">
              <div className="support-modal-welcome-title">Hi 👋</div>
              <div className="support-modal-welcome-text">
                Ask us anything — trial questions, activation, business setup, or issues with the app.
                Real people reply from the Aangan team.
              </div>
            </div>
          )}

          {!loading && messages.map((msg) => {
            // Display side: 'support' if the message was written from the
            // admin panel (marked with senderRole: 'support' by
            // sendAdminMessage). Fallback for messages without the field:
            // any fromId that isn't the business owner is treated as
            // admin. This fallback covers (a) historical messages from
            // before Session 5a shipped and (b) defensive rendering. When
            // fromId equals ownerUid we treat it as "You" — the business
            // owner's own message.
            const fromSupport =
              msg.senderRole === 'support'
              || (msg.fromId && msg.fromId !== ownerUid);

            // Session 5c — a plan card is always admin-authored. Render
            // the card instead of a text bubble; everything else (side,
            // meta line) stays identical.
            const isPlanCard = msg.type === 'plan-card' && fromSupport;

            return (
              <div
                key={msg.id}
                className={`support-msg ${fromSupport ? 'support-msg-them' : 'support-msg-me'} ${isPlanCard ? 'support-msg-card' : ''}`}
              >
                {isPlanCard ? (
                  <PlanCardBubble
                    card={msg.planCard}
                    fallbackText={msg.text}
                    onAccept={handleAcceptPlan}
                    acked={ackedCardIds.includes(msg.planCard?.tierId)}
                    sending={sending}
                  />
                ) : (
                  <div className="support-msg-bubble">
                    {msg.text || (msg.imageUrl ? '📷 Photo' : '')}
                  </div>
                )}
                <div className="support-msg-meta">
                  {fromSupport ? AANGAN_SUPPORT_DISPLAY_NAME : 'You'} · {fmtTime(msg.sentAt)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Composer */}
        <div className="support-modal-composer">
          {sendError && <div className="support-modal-send-error">{sendError}</div>}
          <textarea
            className="support-modal-input"
            placeholder="Type a message…"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            disabled={sending}
          />
          <button
            className="support-modal-send-btn"
            onClick={handleSend}
            disabled={sending || !replyText.trim()}
            type="button"
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
